import { useState, useCallback } from 'react';
import { Chip, IconButton, List, ListItem, ListItemButton, ListItemText, Stack, Tooltip, useMediaQuery } from '@mui/material';
import { DocumentFragment, FileRefFragment } from '../generated/graphql';
import { EditIcon, DownloadIcon } from './icons';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DescriptionIcon from '@mui/icons-material/Description';
import { openDocumentPrintWindow } from '../lib/open-document-print-window';

export type UnifiedItem =
  | { type: 'file'; data: FileRefFragment }
  | { type: 'document'; data: DocumentFragment };

interface UnifiedDocumentListProps {
  items: UnifiedItem[];
  onItemClick: (item: UnifiedItem, isTouchDevice: boolean) => void;
  onEditClick: (item: UnifiedItem) => void;
  disabled?: boolean;
  tagColors?: Record<string, string | null | undefined>;
}

export function UnifiedDocumentList({ items, onItemClick, onEditClick, disabled, tagColors }: UnifiedDocumentListProps) {
  const isTouchDevice = useMediaQuery('(pointer: coarse)');

  return (
    <List disablePadding>
      {items.map((item) => (
        <UnifiedDocumentListItem
          key={`${item.type}-${item.data.id}`}
          item={item}
          isTouchDevice={isTouchDevice}
          onItemClick={onItemClick}
          onEditClick={onEditClick}
          disabled={disabled}
          tagColors={tagColors}
        />
      ))}
    </List>
  );
}

interface UnifiedDocumentListItemProps {
  item: UnifiedItem;
  isTouchDevice: boolean;
  onItemClick: (item: UnifiedItem, isTouchDevice: boolean) => void;
  onEditClick: (item: UnifiedItem) => void;
  disabled?: boolean;
  tagColors?: Record<string, string | null | undefined>;
}

function UnifiedDocumentListItem({ item, isTouchDevice, onItemClick, onEditClick, disabled, tagColors }: UnifiedDocumentListItemProps) {
  const [hovered, setHovered] = useState(false);

  const title = item.type === 'file' ? (item.data.name ?? item.data.id) : item.data.title;
  const subtitle = item.type === 'file' ? (item.data.desc ?? null) : (item.data.description ?? null);
  const tags = item.data.tags ?? [];

  const icon = item.type === 'file' ? <InsertDriveFileIcon fontSize="small" color="action" /> : <DescriptionIcon fontSize="small" color="action" />;

  const handleClick = useCallback(() => {
    if (!disabled) {
      onItemClick(item, isTouchDevice);
    }
  }, [item, isTouchDevice, onItemClick, disabled]);

  const handleEditClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!disabled) onEditClick(item);
    },
    [item, onEditClick, disabled],
  );

  return (
    <ListItem
      disablePadding
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      secondaryAction={
        <Stack direction="row" spacing={0} alignItems="center">
          {/* On touch: always show edit button. On mouse: show on hover */}
          {(isTouchDevice || hovered) && (
            <Tooltip title="Upraviť">
              <IconButton edge="end" size="small" onClick={handleEditClick} disabled={disabled}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {/* On mouse device: show download button for files on hover */}
          {!isTouchDevice && hovered && item.type === 'file' && (
            <Tooltip title="Stiahnuť">
              <IconButton
                edge="end"
                size="small"
                component="a"
                href={item.data.url}
                download={item.data.name ?? true}
                target="_blank"
                rel="noreferrer"
                disabled={disabled}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {/* On touch: always show download button for documents. On mouse: show on hover */}
          {(isTouchDevice || hovered) && item.type === 'document' && (
            <Tooltip title="Stiahnuť / Tlačiť">
              <IconButton
                edge="end"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!disabled) openDocumentPrintWindow(item.data.title, item.data.content);
                }}
                disabled={disabled}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      }
    >
      <ListItemButton onClick={handleClick} disabled={disabled}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mr: 1 }}>
          {icon}
        </Stack>
        <ListItemText
          primary={title}
          secondary={
            subtitle || tags.length > 0 ? (
              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                {subtitle && <span>{subtitle}</span>}
                {tags.length > 0 && (
                  <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                    {tags.map((tag, index) => (
                      <Chip
                        key={`${tag}-${index}`}
                        label={tag}
                        size="small"
                        variant="outlined"
                        sx={{
                          height: 18,
                          '& .MuiChip-label': { px: 0.75, fontSize: '0.6875rem' },
                          ...(tagColors?.[tag.toLocaleLowerCase()]
                            ? {
                                color: tagColors[tag.toLocaleLowerCase()] ?? undefined,
                                borderColor: tagColors[tag.toLocaleLowerCase()] ?? undefined,
                              }
                            : {}),
                        }}
                      />
                    ))}
                  </Stack>
                )}
              </Stack>
            ) : undefined
          }
          secondaryTypographyProps={{ variant: 'caption' }}
        />
      </ListItemButton>
    </ListItem>
  );
}
